import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { Department, DepartmentRequestBody } from './types';

export const departmentsApi = createApi({
  reducerPath: 'departmentsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/departments`,
  }),
  tagTypes: ['Department', 'Departments'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getDepartments: builder.query<Department[], void>({
      query: () => ({
        url: '/',
      }),
      providesTags: () => ['Department', 'Departments'],
    }),
    getDepartmentsI: builder.infiniteQuery<
      Department[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Department', 'Departments'],
    }),
    getDepartmentById: builder.query<Department, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['Department'],
    }),
    createDepartment: builder.mutation<Department, DepartmentRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'Department',
        { type: 'Department', id: result?.id },
      ],
    }),
    updateDepartment: builder.mutation<Department, DepartmentRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'Department',
        { type: 'Department', id: result?.id },
      ],
    }),
    deleteDepartmentById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'Department',
        { type: 'Department', id },
      ],
    }),
  }),
});

export const {
  useGetDepartmentsQuery,
  useGetDepartmentByIdQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentByIdMutation,
} = departmentsApi;

export const useGetDepartmentsIInfiniteQuery = normalizeInfiniteQueryData(
  departmentsApi.useGetDepartmentsIInfiniteQuery,
);
